/* sh-fx.js - SafeHaven ambient seasonal effects, v3 (2026-10-29).
 *
 * Replaces sh-seasonal-v2.js (Shopify Files, minified). Same seasons, same particles, same look; what changed is
 * the cost. Measured on the live site 2026-10-29 (ops/website/gpu-2026-10-29/REPORT.md), v2 was the single biggest
 * GPU cost for returning visitors: turning it off cut GPU work by a third and the main thread from 30% to 16%.
 *
 *  - Fog, wisps and ghosts were radial gradients CREATED AND FILLED for every particle on every frame. They are now
 *    drawn once into small sprites and stamped with drawImage, which looks identical and costs a fraction.
 *  - Motion is time-based, so a capped frame rate changes smoothness, never speed.
 *  - 30 fps at 1x pixel density for everyone. Tier 1 (weak, software-rendered or struggling, html.sh-t1 from
 *    sh-custom.liquid) got half the particles until v88 (2026-10-30); it now gets none, and product pages get none
 *    on any tier. The tier can drop while the page is open (sh-custom's frame-rate probe); this script follows it
 *    live.
 *  - It stops while the age gate is up (the gate covers it completely) and while the tab is hidden.
 *
 * Season: the date decides (Halloween = all of October, autumn leaves in November, ...). Overrides, strongest
 * first: ?fx=<season|off> on the URL, then window.SafeHaven.seasonOverride. Honors prefers-reduced-motion.
 * Sets html.sh-season-<name>, which sh-halloween.css (and future season layers) key off.
 */
(function () {
  if (window.__shFx) { return; }
  window.__shFx = true;
  try { if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) { return; } } catch (e) {}

  var root = document.documentElement;
  var SH = window.SafeHaven || {};

  function resolveSeason(d) {
    d = d || new Date();
    var m = d.getMonth() + 1, day = d.getDate();
    function on(mo, a, b) { return m === mo && day >= a && day <= b; }
    if (on(4, 18, 22)) { return 'fourtwenty'; }
    if (on(12, 30, 31) || on(1, 1, 2)) { return 'newyear'; }
    if (on(2, 10, 15)) { return 'valentines'; }
    if (on(3, 15, 18)) { return 'stpatricks'; }
    if (on(7, 1, 6)) { return 'independence'; }
    if (m === 10) { return 'halloween'; }
    if (m === 12 || m === 1 || m === 2) { return 'winter'; }
    if (m >= 3 && m <= 5) { return 'spring'; }
    if (m >= 6 && m <= 8) { return 'summer'; }
    return 'autumn';
  }

  var urlSeason = null;
  try {
    urlSeason = new URLSearchParams(location.search).get('fx');
    if (!urlSeason && location.hash.indexOf('fx=') > -1) { urlSeason = location.hash.split('fx=')[1]; }
  } catch (e) {}
  var season = urlSeason || SH.seasonOverride || resolveSeason();
  if (season === 'thanksgiving') { season = 'autumn'; } // A music choice only: the player reads the same override. The leaves stay.
  if (season === 'off' || season === 'none') { return; }
  try { root.classList.add('sh-season-' + season); } catch (e) {}

  var SEASONS = {
    winter: { layers: [{ kind: 'flake', colors: ['#EAF1F8', '#CFE4FF', '#A8CBEC'], count: 32, speed: [0.3, 0.9], size: [2, 5], sway: 1.2, opacity: [0.35, 0.85] }] },
    spring: { layers: [{ kind: 'leaf', colors: ['#659E21', '#4C8715', '#8FCE4A'], count: 18, speed: [0.4, 1], size: [7, 13], sway: 1.6, opacity: [0.3, 0.65], spin: true }] },
    summer: { layers: [{ kind: 'wisp', colors: ['#EAF1F8', '#0196F2'], count: 9, speed: [0.15, 0.4], size: [40, 95], sway: 0.6, opacity: [0.05, 0.13] }] },
    autumn: { layers: [{ kind: 'leaf', colors: ['#C98A2B', '#B5651D', '#4C8715', '#8A5A2B'], count: 22, speed: [0.5, 1.2], size: [8, 15], sway: 1.9, opacity: [0.35, 0.75], spin: true }] },
    fourtwenty: { layers: [{ kind: 'leaf', colors: ['#4C8715', '#659E21', '#3D7A12', '#8FCE4A'], count: 26, speed: [0.35, 0.9], size: [9, 16], sway: 1.7, opacity: [0.4, 0.82], spin: true }] },
    newyear: { layers: [{ kind: 'spark', colors: ['#EAF1F8', '#0196F2', '#CFE0EF'], count: 40, speed: [0.2, 0.7], size: [1.5, 3.5], sway: 0.9, opacity: [0.3, 0.95], twinkle: true }] },
    valentines: { layers: [{ kind: 'heart', colors: ['#FF6B8A', '#FF9DB2', '#EAF1F8'], count: 18, speed: [0.3, 0.8], size: [9, 16], sway: 1.4, opacity: [0.3, 0.7] }] },
    stpatricks: { layers: [{ kind: 'clover', colors: ['#4C8715', '#659E21', '#2F6B0F'], count: 20, speed: [0.4, 1], size: [8, 14], sway: 1.6, opacity: [0.35, 0.75], spin: true }] },
    independence: { layers: [{ kind: 'spark', colors: ['#0196F2', '#EAF1F8', '#E34B5A'], count: 36, speed: [0.2, 0.7], size: [1.5, 3.5], sway: 0.9, opacity: [0.3, 0.9], twinkle: true }] },
    halloween: {
      lightning: { minGap: 26000, maxGap: 52000, peak: 0.14, color: '190,160,255' },
      layers: [
        { kind: 'fog', colors: ['#6A3D9A', '#3E2B57', '#22301B'], count: 8, speed: [0.06, 0.18], size: [90, 200], opacity: [0.045, 0.1] },
        { kind: 'ember', colors: ['#FF7A1A', '#FFB347', '#FF5E00'], count: 26, speed: [0.25, 0.7], size: [1.5, 3.5], sway: 0.8, opacity: [0.25, 0.8], twinkle: true },
        { kind: 'ghost', colors: ['#DCE9F5', '#CFE4FF'], count: 4, speed: [0.12, 0.28], size: [26, 44], sway: 1.1, opacity: [0.16, 0.34] },
        { kind: 'bat', colors: ['#171320', '#241A33'], waveGap: [16000, 34000], waveSize: [3, 6], speed: [2.2, 3.6], size: [10, 17], opacity: [0.5, 0.85] }
      ]
    }
  };
  var cfg = SEASONS[season];
  if (!cfg) { return; }
  // v88 (2026-10-30, Kyle: "even on the actual product pages ... it gets really bad in performance"): no particle
  // canvas on product pages. Measured without hardware acceleration (ops/website/mystic-product-2026-10-30), the
  // full-screen canvas over the page cost about as much as the background video. The season class above stays, so the
  // page keeps its seasonal styling; only the moving layer is gone.
  if (/^product/.test(String(SH.template || ''))) { return; }

  // ---------------------------------------------------------------- tier
  function tierNow() { var t = (window.SafeHaven && window.SafeHaven.tier) || 2; return t < 1 ? 1 : t > 3 ? 3 : t; }
  var tier = tierNow();
  // 30 fps and 1x pixels for every tier since 2026-10-30 (Kyle: "shouldn't use more GFX at all for anyone").
  // Tier 3 used to get 60 fps at up to 1.5x density.
  function frameMs() { return 1000 / 30; }
  function density() { return 1; }
  var smallScreen = Math.min(screen.width, screen.height) < 700 || (navigator.hardwareConcurrency || 4) <= 2;
  function lean() { return smallScreen || tier <= 1; }

  // ---------------------------------------------------------------- canvas
  var cv = document.createElement('canvas');
  cv.setAttribute('aria-hidden', 'true');
  cv.className = 'sh-fx';
  cv.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;pointer-events:none;z-index:9998;';
  var ctx = cv.getContext('2d');
  var dpr = density(), W = 0, H = 0;
  function resize() {
    dpr = density();
    W = cv.width = Math.floor(innerWidth * dpr);
    H = cv.height = Math.floor(innerHeight * dpr);
  }
  resize();

  function rnd(a, b) { return a + Math.random() * (b - a); }
  function pick(a) { return a[(Math.random() * a.length) | 0]; }
  function rgb(h) {
    h = h.replace('#', '');
    if (h.length === 3) { h = h.split('').map(function (c) { return c + c; }).join(''); }
    var n = parseInt(h, 16);
    return ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255);
  }

  // Soft round things are stamped from sprites drawn once (v2 built a fresh gradient per particle per frame).
  var SPRITE = 256, sprites = {};
  function sprite(kind, color) {
    var key = kind + color;
    if (sprites[key]) { return sprites[key]; }
    var s = document.createElement('canvas');
    s.width = s.height = SPRITE;
    var c = s.getContext('2d'), r = SPRITE / 2, g = c.createRadialGradient(r, r, 0, r, r, r), col = rgb(color);
    if (kind === 'ghost') {
      g.addColorStop(0, 'rgba(' + col + ',.9)');
      g.addColorStop(0.55, 'rgba(' + col + ',.35)');
      g.addColorStop(1, 'rgba(' + col + ',0)');
    } else {
      g.addColorStop(0, 'rgba(' + col + ',1)');
      g.addColorStop(1, 'rgba(' + col + ',0)');
    }
    c.fillStyle = g;
    c.fillRect(0, 0, SPRITE, SPRITE);
    if (kind === 'ghost') {
      var e = r * 0.16;
      c.fillStyle = 'rgba(15,12,24,.55)';
      c.beginPath(); c.arc(r - e * 1.15, r - e * 0.6, e * 0.5, 0, Math.PI * 2); c.fill();
      c.beginPath(); c.arc(r + e * 1.15, r - e * 0.6, e * 0.5, 0, Math.PI * 2); c.fill();
    }
    sprites[key] = s;
    return s;
  }

  function spawn(L, init) {
    var s = rnd(L.size[0], L.size[1]) * dpr;
    var p = { L: L, size: s, color: pick(L.colors), op: rnd(L.opacity[0], L.opacity[1]), ang: rnd(0, Math.PI * 2),
      va: L.spin ? rnd(-0.02, 0.02) : 0, phase: rnd(0, Math.PI * 2), sway: (L.sway || 0) * dpr, tw: Math.random() };
    if (L.kind === 'fog') {
      p.x = init ? rnd(-s, W + s) : (Math.random() < 0.5 ? -s * 2 : W + s * 2);
      p.y = rnd(H * 0.45, H * 0.95);
      p.vx = rnd(L.speed[0], L.speed[1]) * dpr * (p.x > W / 2 ? -1 : 1);
      p.vy = 0;
    } else if (L.kind === 'ember' || L.kind === 'ghost') {
      p.x = rnd(0, W);
      p.y = init ? rnd(0, H) : H + s * 2;
      p.vy = -rnd(L.speed[0], L.speed[1]) * dpr;
      if (L.kind === 'ghost') { p.life = rnd(0, Math.PI); }
    } else if (L.kind === 'bat') {
      var ltr = Math.random() < 0.5;
      p.x = ltr ? -s * 2 : W + s * 2;
      p.y = rnd(H * 0.08, H * 0.5);
      p.vx = rnd(L.speed[0], L.speed[1]) * dpr * (ltr ? 1 : -1);
      p.vy = 0;
      p.flap = rnd(0, Math.PI * 2);
    } else {
      p.x = rnd(0, W);
      p.y = init ? rnd(0, H) : -s * 2;
      p.vy = rnd(L.speed[0], L.speed[1]) * dpr;
    }
    return p;
  }

  var layers = [];
  function build() {
    layers = [];
    for (var i = 0; i < cfg.layers.length; i++) {
      var L = cfg.layers[i], entry = { L: L, parts: [] };
      if (L.kind === 'bat') {
        entry.nextWave = performance.now() + rnd(3000, 9000);
      } else {
        var n = lean() ? Math.round(L.count * 0.5) : L.count;
        for (var j = 0; j < n; j++) { entry.parts.push(spawn(L, true)); }
      }
      layers.push(entry);
    }
  }
  build();

  function draw(p, op) {
    var L = p.L, k = L.kind;
    ctx.globalAlpha = op < 0 ? 0 : op > 1 ? 1 : op;
    if (k === 'fog' || k === 'wisp' || k === 'ghost') {
      ctx.drawImage(sprite(k, p.color), p.x - p.size, p.y - p.size, p.size * 2, p.size * 2);
      return;
    }
    ctx.save();
    ctx.translate(p.x, p.y);
    if (L.spin) { ctx.rotate(p.ang); }
    ctx.fillStyle = p.color;
    if (k === 'flake' || k === 'spark' || k === 'ember') {
      ctx.beginPath(); ctx.arc(0, 0, p.size * 0.5, 0, Math.PI * 2); ctx.fill();
    } else if (k === 'bat') {
      var w = p.size, lift = Math.sin(p.flap) * 0.9;
      ctx.beginPath(); ctx.ellipse(0, 0, w * 0.18, w * 0.28, 0, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.quadraticCurveTo(-w * 0.55, -w * (0.55 + lift * 0.35), -w, -w * lift * 0.25);
      ctx.quadraticCurveTo(-w * 0.5, w * (0.18 - lift * 0.12), 0, w * 0.08);
      ctx.moveTo(0, 0);
      ctx.quadraticCurveTo(w * 0.55, -w * (0.55 + lift * 0.35), w, -w * lift * 0.25);
      ctx.quadraticCurveTo(w * 0.5, w * (0.18 - lift * 0.12), 0, w * 0.08);
      ctx.fill();
    } else if (k === 'leaf') {
      ctx.beginPath(); ctx.ellipse(0, 0, p.size * 0.32, p.size * 0.6, 0, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha *= 0.5;
      ctx.strokeStyle = 'rgba(0,0,0,0.35)';
      ctx.lineWidth = Math.max(1, p.size * 0.05);
      ctx.beginPath(); ctx.moveTo(0, -p.size * 0.55); ctx.lineTo(0, p.size * 0.55); ctx.stroke();
    } else if (k === 'heart') {
      var sc = p.size / 16;
      ctx.scale(sc, sc);
      ctx.beginPath(); ctx.moveTo(0, 4);
      ctx.bezierCurveTo(-8, -6, -16, 4, 0, 14);
      ctx.bezierCurveTo(16, 4, 8, -6, 0, 4);
      ctx.fill();
    } else if (k === 'clover') {
      var r = p.size * 0.26, pts = [[-r, 0], [r, 0], [0, -r]];
      for (var j = 0; j < 3; j++) { ctx.beginPath(); ctx.arc(pts[j][0], pts[j][1], r, 0, Math.PI * 2); ctx.fill(); }
    }
    ctx.restore();
  }

  var bolt = cfg.lightning ? { next: performance.now() + rnd(cfg.lightning.minGap, cfg.lightning.maxGap), t0: 0, active: false } : null;
  function lightning(now) {
    if (!bolt) { return 0; }
    if (!bolt.active) { if (now >= bolt.next) { bolt.active = true; bolt.t0 = now; } return 0; }
    var t = now - bolt.t0;
    function pulse(x, rise, fall) { return x < 0 || x > rise + fall ? 0 : x < rise ? x / rise : 1 - (x - rise) / fall; }
    var a = Math.max(pulse(t, 120, 260), pulse(t - 500, 90, 410) * 0.7);
    if (t > 1000) { bolt.active = false; bolt.next = now + rnd(cfg.lightning.minGap, cfg.lightning.maxGap); }
    return a * cfg.lightning.peak;
  }

  // One step = one 60 fps frame of v2's motion; k scales it, so 30 fps moves twice as far per frame.
  function step(now, k) {
    ctx.clearRect(0, 0, W, H);
    for (var i = 0; i < layers.length; i++) {
      var en = layers[i], L = en.L;
      if (L.kind === 'bat' && now >= en.nextWave) {
        var n = Math.round(rnd(L.waveSize[0], L.waveSize[1]));
        if (lean()) { n = Math.max(2, Math.round(n * 0.6)); }
        for (var b = 0; b < n; b++) { en.parts.push(spawn(L, false)); }
        en.nextWave = now + rnd(L.waveGap[0], L.waveGap[1]);
      }
      for (var j = en.parts.length - 1; j >= 0; j--) {
        var p = en.parts[j], op = p.op;
        p.phase += 0.01 * k;
        p.ang += p.va * k;
        if (L.kind === 'fog') {
          p.x += p.vx * k;
          if ((p.vx > 0 && p.x - p.size * 2 > W) || (p.vx < 0 && p.x + p.size * 2 < 0)) { en.parts[j] = spawn(L, false); }
        } else if (L.kind === 'ember') {
          p.y += p.vy * k;
          p.x += Math.sin(p.phase) * p.sway * k;
          if (L.twinkle) { op = p.op * (0.45 + 0.55 * Math.sin(p.phase + p.tw * 6)); }
          if (p.y + p.size * 2 < 0) { en.parts[j] = spawn(L, false); }
        } else if (L.kind === 'ghost') {
          p.y += p.vy * k;
          p.x += Math.sin(p.phase * 0.6) * p.sway * k;
          p.life += 0.0035 * k;
          op = p.op * Math.max(0, Math.sin(p.life));
          if (p.life >= Math.PI || p.y + p.size * 2 < 0) { en.parts[j] = spawn(L, false); continue; }
        } else if (L.kind === 'bat') {
          p.x += p.vx * k;
          p.flap += 0.28 * k;
          p.y += Math.sin(p.phase * 3) * 0.6 * dpr * k;
          if ((p.vx > 0 && p.x - p.size * 2 > W) || (p.vx < 0 && p.x + p.size * 2 < 0)) { en.parts.splice(j, 1); continue; }
        } else {
          p.y += p.vy * k;
          p.x += Math.sin(p.phase) * p.sway * k;
          if (L.twinkle) { op = p.op * (0.45 + 0.55 * Math.sin(p.phase + p.tw * 6)); }
          if (p.y - p.size * 2 > H) { en.parts[j] = spawn(L, false); }
        }
        draw(p, op);
      }
    }
    ctx.globalAlpha = 1;
    var la = lightning(now);
    if (la > 0) {
      var lg = ctx.createRadialGradient(W * 0.5, 0, 0, W * 0.5, 0, H * 1.1);
      lg.addColorStop(0, 'rgba(' + cfg.lightning.color + ',' + la + ')');
      lg.addColorStop(1, 'rgba(' + cfg.lightning.color + ',0)');
      ctx.fillStyle = lg;
      ctx.fillRect(0, 0, W, H);
    }
  }

  // ---------------------------------------------------------------- clock
  // Capped by sleeping between frames, not by skipping them: a requestAnimationFrame loop that runs every refresh
  // and draws on some of them still makes the browser render a page frame every refresh (style for every CSS
  // animation on the page, each time). So after each draw: a timer for most of the gap, then one rAF. The timer
  // ends 4 ms early, so the rAF lands on the first refresh at or after the due time (60 Hz: exactly 30/60 fps;
  // 144 Hz: ~29/72 fps).
  var raf = 0, timer = 0, last = 0;
  function frame(now) {
    raf = 0;
    var k = last ? Math.min((now - last) / (1000 / 60), 4) : 1; // after a stall, catch up at most 4 frames' worth
    last = now;
    step(now, k);
    var wait = Math.max(0, frameMs() - 4 - (performance.now() - now));
    timer = setTimeout(function () { timer = 0; if (!blocked()) { raf = requestAnimationFrame(frame); } }, wait);
  }
  // v88: tier 1 (software rendering, or a machine that stuttered) gets no particles at all, and the canvas leaves the
  // page so it is not composited over the video every frame. The video itself plays for everyone (Kyle 2026-10-29).
  function blocked() { return document.hidden || root.classList.contains('sh-ag-lock') || tier <= 1; }
  function start() { if (!raf && !timer && !blocked()) { last = 0; raf = requestAnimationFrame(frame); } }
  function stop() {
    if (raf) { cancelAnimationFrame(raf); raf = 0; }
    if (timer) { clearTimeout(timer); timer = 0; }
  }
  function sync() { cv.style.display = tier <= 1 ? 'none' : ''; if (blocked()) { stop(); } else { start(); } }

  document.addEventListener('visibilitychange', sync);
  document.addEventListener('sh:gate', sync);
  document.addEventListener('sh:tier', function () {
    var was = lean(), d0 = dpr;
    tier = tierNow();
    if (density() !== d0) { resize(); build(); } else if (lean() !== was) { build(); }
    sync();
  });
  window.addEventListener('resize', resize);
  function mount() { (document.body || root).appendChild(cv); sync(); }
  if (document.body) { mount(); } else { document.addEventListener('DOMContentLoaded', mount); }
})();
